import { useRef, useState } from 'react';
import { api, type StockAudioItem } from '../../api/client';
import { useEditor } from '../../state/editorStore';

function srcLabel(source: string): string {
  if (source.startsWith('openverse')) return 'Openverse';
  if (source === 'jamendo') return 'Jamendo';
  return source;
}
function dur(sec: number): string {
  if (!sec) return '';
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

/**
 * "Add a music track" dialog: upload from device, or search our integrated
 * royalty-free sources (Jamendo + Openverse — ccMixter/Freesound/…) and preview
 * (play) a track before adding it as the project's background music.
 */
export function StudioMusicAddDialog({
  open,
  onClose,
  onUpload,
  onPickTrack,
}: {
  open: boolean;
  onClose: () => void;
  onUpload: () => void;
  onPickTrack: (item: StockAudioItem) => Promise<void>;
}) {
  const projectId = useEditor((s) => s.projectId);
  const [q, setQ] = useState('');
  const [items, setItems] = useState<StockAudioItem[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [addingId, setAddingId] = useState<string | null>(null);
  const [added, setAdded] = useState<Set<string>>(new Set());
  const [playingId, setPlayingId] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  if (!open) return null;

  const search = async () => {
    if (!q.trim() || !projectId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.musicSearch(projectId, q.trim());
      setItems(res.items);
      if (!res.enabled) setError('Music search is not configured on the server.');
      else if (res.items.length === 0) setError('No tracks — try a different mood or genre.');
    } catch {
      setError('Search failed. Try again.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const togglePlay = (item: StockAudioItem) => {
    const el = audioRef.current;
    if (!el) return;
    if (playingId === item.id) {
      el.pause();
      setPlayingId(null);
      return;
    }
    el.src = item.audioUrl;
    el.play().then(() => setPlayingId(item.id)).catch(() => setError('Could not play this track (preview blocked).'));
  };

  const pick = async (item: StockAudioItem) => {
    if (addingId) return;
    setAddingId(item.id);
    try {
      await onPickTrack(item);
      setAdded((s) => new Set(s).add(item.id));
    } catch {
      setError('Could not add that track.');
    } finally {
      setAddingId(null);
    }
  };

  const close = () => {
    audioRef.current?.pause();
    setPlayingId(null);
    onClose();
  };

  return (
    <div className="bad-backdrop" onClick={close}>
      <div className="bad" onClick={(e) => e.stopPropagation()} role="dialog" aria-label="Add music">
        <div className="bad-head">
          <strong>Add music</strong>
          <button className="bad-x" onClick={close} aria-label="Close">✕</button>
        </div>

        <div className="bad-controls">
          <button className="bad-upload" onClick={() => { onUpload(); close(); }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M12 16V4M6 10l6-6 6 6M4 20h16" />
            </svg>
            Upload from device
          </button>
          <span className="bad-or">or search free music</span>
        </div>

        <div className="bad-searchrow">
          <input
            className="bad-input"
            placeholder="Search a mood or genre — e.g. uplifting, lofi, cinematic…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && search()}
            autoFocus
          />
          <button className="bad-go" onClick={search} disabled={!q.trim() || loading}>
            {loading ? '…' : 'Search'}
          </button>
        </div>

        <div className="bad-results">
          {error && <div className="bad-msg">{error}</div>}
          {!error && items === null && <div className="bad-msg">Search for background music to add.</div>}
          {items && items.length > 0 && (
            <div className="bmd-list">
              {items.map((t) => (
                <div key={t.id} className="bmd-row">
                  <button className="bmd-play" onClick={() => togglePlay(t)} aria-label={playingId === t.id ? 'Pause' : 'Play'}>
                    {playingId === t.id ? '⏸' : '▶'}
                  </button>
                  <div className="bmd-meta">
                    <span className="bmd-title">{t.title}</span>
                    <span className="bmd-sub">{t.artist} · {srcLabel(t.source)}{t.durationSec ? ` · ${dur(t.durationSec)}` : ''}</span>
                  </div>
                  <button
                    className="bmd-add"
                    onClick={() => pick(t)}
                    disabled={addingId === t.id || added.has(t.id)}
                  >
                    {addingId === t.id ? 'Adding…' : added.has(t.id) ? '✓ Added' : '+ Add'}
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
        <audio ref={audioRef} onEnded={() => setPlayingId(null)} hidden />
      </div>
    </div>
  );
}
